import { useSelector } from 'react-redux';

import {
  selectEntityScope,
  selectEntityTree,
  selectIsScoped,
  selectParentEntityId,
  selectSelectedEntityId,
  selectSelectedOrgName,
  selectSkipCheckout,
  type EntityScopeState,
  type ScopeRoot,
} from '../slice/entityScopeSlice';
import { scopeRoleOf } from '../services/current-context';
import type { EntityTreeNode } from '../types/order.types';

/**
 * The React face of the entity-scope source (DV-3013 spec §3.1).
 *
 * Use these instead of reading `localStorage.selectedOrg` / `selectedEntity` /
 * `parentEntityId` / `hasWatermark` / `skipCheckout` / `entityTree` at mount.
 * A mount-time read is correct today ONLY because a switch reloads the
 * document; that stops being true in PR 3 (spec §2.2).
 *
 * Non-React callers want `getEntityScope()` from services/entity-scope-read.ts.
 */
export const useEntityScope = (): EntityScopeState => useSelector(selectEntityScope);
export const useSelectedEntityId = (): string | null => useSelector(selectSelectedEntityId);
export const useSelectedOrgName = (): string | null => useSelector(selectSelectedOrgName);
export const useParentEntityId = (): string | null => useSelector(selectParentEntityId);
export const useIsScoped = (): boolean => useSelector(selectIsScoped);
export const useSkipCheckout = (): boolean => useSelector(selectSkipCheckout);
export const useEntityTree = (): EntityTreeNode | null => useSelector(selectEntityTree);

// Selects the role itself, a string, so a scope change that leaves the role
// alone (a rename, a tree load, a stamp) does not re-render the caller.
const selectScopeRole = (s: ScopeRoot): string | null => scopeRoleOf(selectEntityScope(s));

/**
 * The caller's role in the account this tab is looking at: `getScopeRole()`,
 * re-read whenever the scope changes, so a switch or a Log In As re-renders.
 */
export const useScopeRole = (): string | null => useSelector(selectScopeRole);
